import React, { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';

import { Popover } from 'ui-components/components/popover';
import { noop } from 'ui-components/default-props/noop';

import { EditNotePopoverContent } from './edit-note-popover-content';
import style from './note-icon.module.less';
import { ViewNotePopoverContent } from './view-note-popover-content';

/**
 * @param {object} props
 * @param {string} [props.fulfillmentPlanId]
 * @param {import('@fortawesome/fontawesome-common-types').IconDefinition} props.icon
 * @param {import('../../../../prop-types/message').MessagePropType} [props.note]
 * @param {boolean} [props.disableEdit]
 * @param {string} [props.type]
 * @param {import('../../../../prop-types/intl').MessageDescriptorPropType} props.titleTranslation
 * @param {(value: string) => void} [props.onEditAction]
 * @returns {JSX.Element}
 */
const NoteIcon = ({ fulfillmentPlanId = '', icon, note, disableEdit = false, type = '', titleTranslation, onEditAction }) => {
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const [displayEditPopover, setDisplayEditPopover] = useState(false);
  const popoverWrapperRef = useRef(null);
  const targetIconRef = useRef(null);

  const timeoutId = {};

  const togglePopover = useCallback(
    (toState, displayEditPopoverState = false) => {
      setPopoverIsOpen(note && toState);
      setDisplayEditPopover(!disableEdit && displayEditPopoverState);
    },
    [note, disableEdit],
  );

  const editNote = () => {
    setDisplayEditPopover(true);
  };

  const onTargetClick = (e) => {
    e.stopPropagation();
    if (!disableEdit) {
      if (!popoverIsOpen) {
        setPopoverIsOpen(true);
      }
      setDisplayEditPopover(true);
    }
  };

  const onBlur = useCallback(
    (e) => {
      e.stopPropagation();
      timeoutId.onBlur = setTimeout(() => {
        timeoutId.onBlur = null;
        const currentPopoverEl = popoverWrapperRef.current;
        const currentTargetIconEl = targetIconRef.current;

        const popoverNotInFocus =
          currentPopoverEl &&
          currentPopoverEl.containerEl &&
          !currentPopoverEl.containerEl.matches(':hover') &&
          !currentPopoverEl.containerEl.matches(':focus');
        const noteIconNotInFocus = currentTargetIconEl && !currentTargetIconEl.matches(':hover') && !currentTargetIconEl.matches(':focus');

        if (!displayEditPopover && popoverNotInFocus && noteIconNotInFocus) {
          togglePopover(false);
        }
      }, 100);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- timeoutId changes every render
    [togglePopover, timeoutId, displayEditPopover],
  );

  const onTargetHover = useCallback(
    (e) => {
      e.stopPropagation();
      timeoutId.onHover = setTimeout(() => {
        timeoutId.onHover = null;
        const disableEditWithNote = disableEdit && note && note.message !== '';
        if (!displayEditPopover || disableEditWithNote) {
          const currentTargetIconEl = targetIconRef.current;
          if (currentTargetIconEl && currentTargetIconEl.matches(':hover')) {
            togglePopover(true);
          }
        }
      }, 250);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- timeoutId changes every render
    [disableEdit, togglePopover, note, displayEditPopover, timeoutId],
  );

  const target = () => {
    return (
      <div
        ref={targetIconRef}
        className={classnames(style['note-icon'], {
          [style['has-notes']]: note,
        })}
        data-testid='note-icon'
        onClick={onTargetClick}
        onMouseEnter={note ? onTargetHover : noop}
        onMouseLeave={note ? onBlur : noop}
      >
        <FontAwesomeIcon icon={icon} />
      </div>
    );
  };

  const realNote = note || {
    message: '',
    createdDateTime: '',
    additionalCount: 0,
  };
  const { message = '' } = realNote;

  const stopPropagate = (e) => e.stopPropagation();

  const viewNoteProps = {
    disableEdit,
    titleTranslation,
    note: realNote,
    editNote,
    stopPropagate,
    onBlur,
  };

  const editNoteProps = {
    fulfillmentPlanId,
    icon,
    note,
    disableEdit,
    type,
    titleTranslation,
    message,
    displayEditPopover,
    togglePopover,
    stopPropagate,
    onEditAction,
  };

  const bodyContent = displayEditPopover ? <EditNotePopoverContent {...editNoteProps} /> : <ViewNotePopoverContent {...viewNoteProps} />;

  const onOuterAction = useCallback(() => togglePopover(false), [togglePopover]);

  // !important is necessary to overwrite base calculated transform style
  const popoverStyles = 'transform: translateX(5px)!important;';

  const popoverProps = {
    isOpen: popoverIsOpen,
    preferPlace: 'left',
    onOuterAction,
    enterExitTransitionDurationMs: 0,
    body: bodyContent,
    appendTarget: document.querySelector('#app-root'),
    ref: popoverWrapperRef,
    popoverStyles,
  };

  return <Popover {...popoverProps}>{target()}</Popover>;
};

export default NoteIcon;
